/**
 * Tour API Hooks
 * Phase 66-01: Tour Infrastructure Foundation
 *
 * React Query hooks for managing user tour progress.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import type {
  UserTourProgress,
  StartTourRequest,
  UpdateTourProgressRequest,
} from '@shared/types/tour';

// =====================================================
// Query Keys
// =====================================================

const tourKeys = {
  all: ['tour-progress'] as const,
  user: () => [...tourKeys.all, 'current'] as const,
};

// =====================================================
// Queries
// =====================================================

/**
 * Fetch current user's tour progress
 * Returns null if no progress exists yet
 */
export function useUserTourProgress() {
  return useQuery({
    queryKey: tourKeys.user(),
    queryFn: async (): Promise<UserTourProgress | null> => {
      const {
        data: { user },
      } = await supabase.auth.getUser();

      if (!user) {
        throw new Error('Not authenticated');
      }

      const { data, error } = await supabase
        .from('user_tour_progress')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error) {
        console.error('[useUserTourProgress] Error fetching tour progress:', error);
        throw error;
      }

      return data as UserTourProgress | null;
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

// =====================================================
// Mutations
// =====================================================

/**
 * Start a new tour or reset existing progress
 * Uses upsert to handle both create and reset cases
 */
export function useStartTour() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: StartTourRequest): Promise<UserTourProgress> => {
      const {
        data: { user },
      } = await supabase.auth.getUser();

      if (!user) {
        throw new Error('Not authenticated');
      }

      // Upsert: insert or update on conflict
      const { data, error } = await supabase
        .from('user_tour_progress')
        .upsert(
          {
            user_id: user.id,
            tour_type: request.tour_type,
            current_step: 0,
            is_completed: false,
            is_skipped: false,
            started_at: new Date().toISOString(),
            completed_at: null,
          },
          {
            onConflict: 'user_id',
          }
        )
        .select()
        .single();

      if (error) {
        console.error('[useStartTour] Error starting tour:', error);
        throw error;
      }

      return data as UserTourProgress;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tourKeys.user() });
    },
  });
}

/**
 * Update tour progress (step, completion, skip status)
 */
export function useUpdateTourProgress() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: UpdateTourProgressRequest): Promise<UserTourProgress> => {
      const {
        data: { user },
      } = await supabase.auth.getUser();

      if (!user) {
        throw new Error('Not authenticated');
      }

      const updatePayload: Record<string, unknown> = {};

      if (request.current_step !== undefined) {
        updatePayload.current_step = request.current_step;
      }
      if (request.is_completed !== undefined) {
        updatePayload.is_completed = request.is_completed;
      }
      if (request.is_skipped !== undefined) {
        updatePayload.is_skipped = request.is_skipped;
      }
      if (request.completed_at !== undefined) {
        updatePayload.completed_at = request.completed_at;
      }

      const { data, error } = await supabase
        .from('user_tour_progress')
        .update(updatePayload)
        .eq('user_id', user.id)
        .select()
        .single();

      if (error) {
        console.error('[useUpdateTourProgress] Error updating tour progress:', error);
        throw error;
      }

      return data as UserTourProgress;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tourKeys.user() });
    },
  });
}
